/**
 * Barra de lotação do grupo.
 *
 * **Não existe barra de progresso no design system** — e este caso não pedia uma genérica:
 * o que a tela precisa mostrar não é "quanto por cento", é "quanto falta para o grupo sair
 * do rodízio". Então a barra mede contra o **limite de segurança**, nunca contra a
 * capacidade do WhatsApp.
 *
 * Três regras do DESIGN_SYSTEM §12 que o componente respeita:
 *  - nada de `rounded-full` (fica `rounded-sm`);
 *  - cor **não** é canal único: o número vem escrito ao lado, sempre;
 *  - sem gradiente, sem animação de contador.
 *
 * Sem contagem, a barra não aparece: inventar uma barra vazia diria "grupo vazio", e
 * "nunca sincronizei" não é "está vazio" (invariante 8).
 */
import { cn, NO_DATA_DASH } from '@mgdlead/ui';

import {
  occupancy,
  occupancyLabel,
  occupancyTone,
  seatsLeft,
} from '../../../features/group/group-ui';
import type { Group } from '../../../lib/api';

const TRACK_TONE: Record<'success' | 'warning' | 'danger' | 'muted', string> = {
  success: 'bg-success',
  warning: 'bg-warning',
  danger: 'bg-danger',
  muted: 'bg-muted',
};

export interface OccupancyBarProps {
  group: Pick<Group, 'participantsCount' | 'safetyThreshold' | 'maxParticipants'>;
  /** `true` encolhe para caber na linha da tabela. */
  compact?: boolean;
  className?: string;
}

export function OccupancyBar({ group, compact = false, className }: OccupancyBarProps) {
  const value = occupancy(group);
  const left = seatsLeft(group);
  const tone = occupancyTone(value);

  if (value === null || group.participantsCount === null) {
    return (
      <span className={cn('nums text-muted-foreground', className)}>
        <span aria-hidden="true">{NO_DATA_DASH}</span>
        <span className="sr-only">Sem contagem ainda — este grupo nunca foi sincronizado.</span>
      </span>
    );
  }

  return (
    <div className={cn('flex flex-col gap-1', className)}>
      <div
        role="img"
        aria-label={occupancyLabel(group)}
        className={cn('w-full overflow-hidden rounded-sm bg-muted', compact ? 'h-1.5' : 'h-2')}
      >
        <div
          className={cn('h-full rounded-sm', TRACK_TONE[tone])}
          style={{ width: `${Math.round(value * 100)}%` }}
        />
      </div>

      <p className="nums text-2xs text-muted-foreground">
        {/* O número por extenso: quem lê a tela não precisa decifrar a cor. */}
        <span className="text-foreground">{group.participantsCount}</span> de{' '}
        {group.safetyThreshold}
        {left === 0 ? (
          <span className="text-danger"> · sem vagas</span>
        ) : (
          <span>
            {' '}
            · {left} {left === 1 ? 'vaga' : 'vagas'}
          </span>
        )}
      </p>
    </div>
  );
}
